import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { getErrorText, showErrorMessage, showSuccessMessage } from 'helpers';
import { useDispatch, useSelector } from 'react-redux';
import { principalIdSelector, userEmailSelector } from 'store/selectors';
import {
  accountLookup,
  exportToBlueShift,
  exportToBrevo,
  exportToCleverReach,
  exportToConstantContact,
  exportToDotdigital,
  exportToEcoMail,
  exportToIContact,
  exportToKlaviyo,
  exportToMailJet,
  exportToMoosend,
  exportToPabbly,
  exportToRule,
  exportToSelzy,
  exportToSendGrid,
  exportToSendpulse,
  exportToSendwithus,
  saveMailchimpDraft,
  saveZohoCampaignDraft,
  saveZohoMailDraft,
  exportToOneSignal,
  exportToElasticEmail,
} from 'store/actions';
import cx from 'classnames';
import SelectBox from 'components/SelectBox';
import {
  openCleverReachAuthPopup,
  openConstantContactAuthPopup,
  openMailchimpAuthPopup,
  openZohoCampaignAuthPopup,
  openZohoMailAuthPopup,
} from '../utils';
import './ExportModalEmailProvider.sass';

export const ExportModalEmailProvider = ({
  templateId,
  isLoading,
  setIsLoading,
  selectedItem,
  setSelectedItem,
}) => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const EMAIL_PROVIDERS_NAMES = [
    { name: 'mailchimp', value: 'Mailchimp' },
    { name: 'mailjet', value: 'Mailjet' },
    { name: 'sendgrid', value: 'SendGrid' },
    { name: 'blueshift', value: 'Blueshift' },
    { name: 'zohomail', value: 'Zoho Mail' },
    { name: 'zohocampaign', value: 'Zoho Campaigns' },
    { name: 'klaviyo', value: 'Klaviyo' },
    { name: 'icontact', value: 'iContact' },
    { name: 'brevo', value: 'Brevo' },
    { name: 'ecomail', value: 'EcoMail' },
    { name: 'constantcontact', value: 'Constant Contact' },
    { name: 'dotdigital', value: 'Dotdigital' },
    { name: 'sendwithus', value: 'SendWithUs' },
    { name: 'sendpulse', value: 'SendPulse' },
    { name: 'rule', value: 'Rule' },
    { name: 'moosend', value: 'Moosend' },
    { name: 'pabbly', value: 'Pabbly' },
    { name: 'selzy', value: 'Selzy' },
    { name: 'cleverreach', value: 'CleverReach' },
    { name: 'onesignal', value: 'OneSignal' },
    { name: 'elasticemail', value: 'Elastic Email' },
  ];

  const REGIONS = [
    {
      name: 'United states',
      value: 'com',
    },
    {
      name: 'Europe',
      value: 'eu',
    },
    {
      name: 'India',
      value: 'in',
    },
    {
      name: 'Australia',
      value: 'au',
    },
    {
      name: 'Japan',
      value: 'jp',
    },
    {
      name: 'China',
      value: 'cn',
    },
  ];

  const DOTDIGITAL_REGIONS = [
    {
      name: 'Europe',
      value: 'r1',
    },
    {
      name: 'North America',
      value: 'r2',
    },
    {
      name: 'Asia Pacific',
      value: 'r3',
    },
  ];

  const [elasticEmailInfo, setElasticEmailInfo] = useState({
    apiKey: '',
  });
  const [oneSignalInfo, setOneSignalInfo] = useState({
    apiKey: '',
    appId: '',
  });
  const [selzyInfo, setSelzyInfo] = useState({
    apiKey: '',
  });
  const [pabblyInfo, setPabblyInfo] = useState({
    apiKey: '',
  });
  const [ecoMailInfo, setEcoMailInfo] = useState({
    apiKey: '',
  });
  const [brevoInfo, setBrevoInfo] = useState({
    apiKey: '',
  });
  const [iContactInfo, setIContactInfo] = useState({
    accountId: '',
    clientFolderId: '',
    appId: '',
    userName: '',
    password: '',
  });
  const [mailJetInfo, setMailJetInfo] = useState({
    apiKey: '',
    secretKey: '',
    author: '',
    purposes: ['marketing'],
  });
  const [sendGridInfo, setSendGridInfo] = useState({
    apiKey: '',
  });
  const [blueShiftInfo, setBlueShiftInfo] = useState({
    userAPIKey: '',
    authorName: '',
  });
  const [klaviyoInfo, setKlaviyoInfo] = useState({
    privateAPIKey: '',
  });
  const [sendwithusInfo, setSendwithusInfo] = useState({
    apiKey: '',
  });
  const [sendpulseInfo, setSendpulseInfo] = useState({
    id: '',
    secret: '',
  });
  const [dotdigitalInfo, setDotdigitalInfo] = useState({
    apiUserName: '',
    apiPassword: '',
    region: 'r1',
  });
  const [ruleInfo, setRuleInfo] = useState({
    apiKey: '',
    fromName: '',
    fromEmail: '',
    identifier: 'exportEmail',
  });
  const [moosendInfo, setMoosendInfo] = useState({
    senderEmail: '',
    apiKey: '',
    mailingList: [
      {
        MailingListID: '',
        SegmentID: '',
      },
    ],
  });
  const [zohoMailRegion, setZohoMailRegion] = useState('eu');
  const [zohoCampaignRegion, setZohoCampaignRegion] = useState('eu');

  const userId = useSelector(principalIdSelector);
  const userEmail = useSelector(userEmailSelector);

  const showEmailProviderName = (name, array) => {
    const found = array.find((item) => item.name === name);
    return found ? found.value : '';
  };

  return (
    <div className="export-modal-email-provider">
      <div className="export-modal__items">
        <button
          key="mailchimp-email-provider"
          disabled={isLoading && selectedItem === 'mailchimp'}
          onClick={async () => {
            setIsLoading(true);
            setSelectedItem('mailchimp');

            const payload = {
              name: userEmail,
              type: 'MAILCHIMP',
            };

            try {
              const { authLink } = await dispatch(
                accountLookup({ userId, payload }),
              ).unwrap();

              openMailchimpAuthPopup(
                authLink,
                dispatch,
                saveMailchimpDraft,
                templateId,
                showSuccessMessage,
                showErrorMessage,
                getErrorText,
                setIsLoading,
                setSelectedItem,
              );
            } catch {
              showErrorMessage(getErrorText());
            }
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'mailchimp',
            active: selectedItem === 'mailchimp',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/mailchimp.svg"
            alt={`${showEmailProviderName('mailchimp', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('mailchimp', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="mailjet-email-provider"
          disabled={isLoading && selectedItem === 'mailjet'}
          onClick={async () => setSelectedItem('mailjet')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'mailjet',
            active: selectedItem === 'mailjet',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/mailjet.svg"
            alt={`${showEmailProviderName('mailjet', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('mailjet', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="sendgrid-email-provider"
          disabled={isLoading && selectedItem === 'sendgrid'}
          onClick={async () => setSelectedItem('sendgrid')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'sendgrid',
            active: selectedItem === 'sendgrid',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/sendgrid.svg"
            alt={`${showEmailProviderName('sendgrid', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('sendgrid', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="blueshift-email-provider"
          disabled={isLoading && selectedItem === 'blueshift'}
          onClick={async () => setSelectedItem('blueshift')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'blueshift',
            active: selectedItem === 'blueshift',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/blueshift.svg"
            alt={`${showEmailProviderName('blueshift', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('blueshift', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="zohomail-email-provider"
          disabled={isLoading && selectedItem === 'zohomail'}
          onClick={async () => setSelectedItem('zohomail')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'zohomail',
            active: selectedItem === 'zohomail',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/zoho-mail.svg"
            alt={`${showEmailProviderName('zohomail', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('zohomail', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="zoho-campaign-email-provider"
          disabled={isLoading && selectedItem === 'zoho-campaign'}
          onClick={async () => setSelectedItem('zohocampaign')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'zohocampaign',
            active: selectedItem === 'zohocampaign',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/zoho.svg"
            alt={`${showEmailProviderName(
              'zohocampaign',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('zohocampaign', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="klaviyo-email-provider"
          disabled={isLoading && selectedItem === 'klaviyo'}
          onClick={async () => setSelectedItem('klaviyo')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'klaviyo',
            active: selectedItem === 'klaviyo',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/klaviyo.svg"
            alt={`${showEmailProviderName('klaviyo', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('klaviyo', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="icontact-email-provider"
          disabled={isLoading && selectedItem === 'icontact'}
          onClick={async () => setSelectedItem('icontact')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'icontact',
            active: selectedItem === 'icontact',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/icontact.svg"
            alt={`${showEmailProviderName('icontact', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('icontact', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="brevo-email-provider"
          disabled={isLoading && selectedItem === 'brevo'}
          onClick={async () => setSelectedItem('brevo')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'brevo',
            active: selectedItem === 'brevo',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/brevo.svg"
            alt={`${showEmailProviderName('brevo', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('brevo', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="ecomail-email-provider"
          disabled={isLoading && selectedItem === 'ecomail'}
          onClick={async () => setSelectedItem('ecomail')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'ecomail',
            active: selectedItem === 'ecomail',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/ecomail.svg"
            alt={`${showEmailProviderName('ecomail', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('ecomail', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="constantcontact-email-provider"
          disabled={isLoading && selectedItem === 'constantcontact'}
          onClick={async () => {
            setIsLoading(true);
            setSelectedItem('constantcontact');

            const payload = {
              name: userEmail,
              type: 'CONSTANT_CONTACT',
            };

            try {
              const { authLink } = await dispatch(
                accountLookup({ userId, payload }),
              ).unwrap();

              openConstantContactAuthPopup(
                authLink,
                dispatch,
                exportToConstantContact,
                templateId,
                showSuccessMessage,
                showErrorMessage,
                getErrorText,
                setIsLoading,
                setSelectedItem,
              );
            } catch {
              showErrorMessage(getErrorText());
            }
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'constantcontact',
            active: selectedItem === 'constantcontact',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/constant-contact.svg"
            alt={`${showEmailProviderName(
              'constantcontact',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('constantcontact', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="dotdigital-email-provider"
          disabled={isLoading && selectedItem === 'dotdigital'}
          onClick={async () => setSelectedItem('dotdigital')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'dotdigital',
            active: selectedItem === 'dotdigital',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/zoho.svg"
            alt={`${showEmailProviderName(
              'dotdigital',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('dotdigital', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="sendwithus-email-provider"
          disabled={isLoading && selectedItem === 'sendwithus'}
          onClick={async () => setSelectedItem('sendwithus')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'sendwithus',
            active: selectedItem === 'sendwithus',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/sendwithus.svg"
            alt={`${showEmailProviderName(
              'sendwithus',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('sendwithus', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="sendpulse-email-provider"
          disabled={isLoading && selectedItem === 'sendpulse'}
          onClick={async () => setSelectedItem('sendpulse')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'sendpulse',
            active: selectedItem === 'sendpulse',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/sendpulse.svg"
            alt={`${showEmailProviderName('sendpulse', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('sendpulse', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="rule-email-provider"
          disabled={isLoading && selectedItem === 'rule'}
          onClick={async () => setSelectedItem('rule')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'rule',
            active: selectedItem === 'rule',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/rule.svg"
            alt={`${showEmailProviderName('rule', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('rule', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="moosend-email-provider"
          disabled={isLoading && selectedItem === 'moosend'}
          onClick={async () => setSelectedItem('moosend')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'moosend',
            active: selectedItem === 'moosend',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/moosend.svg"
            alt={`${showEmailProviderName('moosend', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('moosend', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="pabbly-email-provider"
          disabled={isLoading && selectedItem === 'pabbly'}
          onClick={async () => setSelectedItem('pabbly')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'pabbly',
            active: selectedItem === 'pabbly',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/pabbly.svg"
            alt={`${showEmailProviderName('pabbly', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('pabbly', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="selzy-email-provider"
          disabled={isLoading && selectedItem === 'selzy'}
          onClick={async () => setSelectedItem('selzy')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'selzy',
            active: selectedItem === 'selzy',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/selzy.svg"
            alt={`${showEmailProviderName('selzy', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('selzy', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="cleverreach-email-provider"
          disabled={isLoading && selectedItem === 'cleverreach'}
          onClick={async () => {
            setIsLoading(true);
            setSelectedItem('cleverreach');

            const payload = {
              name: userEmail,
              type: 'CLEVER_REACH',
            };

            try {
              const { authLink } = await dispatch(
                accountLookup({ userId, payload }),
              ).unwrap();

              openCleverReachAuthPopup(
                authLink,
                dispatch,
                exportToCleverReach,
                templateId,
                showSuccessMessage,
                showErrorMessage,
                getErrorText,
                setIsLoading,
                setSelectedItem,
              );
            } catch {
              showErrorMessage(getErrorText());
            }
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'cleverreach',
            active: selectedItem === 'cleverreach',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/cleverreach.svg"
            alt={`${showEmailProviderName(
              'cleverreach',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('cleverreach', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="onesignal-email-provider"
          disabled={isLoading && selectedItem === 'onesignal'}
          onClick={async () => setSelectedItem('onesignal')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'onesignal',
            active: selectedItem === 'onesignal',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/onesignal.svg"
            alt={`${showEmailProviderName('onesignal', EMAIL_PROVIDERS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('onesignal', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
        <button
          key="elasticemail-email-provider"
          disabled={isLoading && selectedItem === 'elasticemail'}
          onClick={async () => setSelectedItem('elasticemail')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'elasticemail',
            active: selectedItem === 'elasticemail',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/elastic-email.svg"
            alt={`${showEmailProviderName(
              'elasticemail',
              EMAIL_PROVIDERS_NAMES,
            )}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showEmailProviderName('elasticemail', EMAIL_PROVIDERS_NAMES)}
          </span>
        </button>
      </div>
      <div
        className={cx('export-modal-email-provider__details', {
          active:
            selectedItem &&
            (selectedItem === 'mailjet' ||
              selectedItem === 'sendgrid' ||
              selectedItem === 'zohomail' ||
              selectedItem === 'zohocampaign' ||
              selectedItem === 'brevo' ||
              selectedItem === 'icontact' ||
              selectedItem === 'klaviyo' ||
              selectedItem === 'ecomail' ||
              selectedItem === 'dotdigital' ||
              selectedItem === 'sendwithus' ||
              selectedItem === 'sendpulse' ||
              selectedItem === 'moosend' ||
              selectedItem === 'rule' ||
              selectedItem === 'pabbly' ||
              selectedItem === 'selzy' ||
              selectedItem === 'onesignal' ||
              selectedItem === 'elasticemail' ||
              selectedItem === 'blueshift'),
        })}>
        <h2>
          {intl.formatMessage({ id: 'export-modal.options-title' })}
          {showEmailProviderName(selectedItem, EMAIL_PROVIDERS_NAMES)}
        </h2>
        {selectedItem === 'elasticemail' && (
          <>
            <label>
              <FormattedMessage id="export-modal.elasticemail.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setElasticEmailInfo({
                  ...elasticEmailInfo,
                  apiKey: e.target.value,
                })
              }
              name="elasticemail-api-key"
            />
          </>
        )}
        {selectedItem === 'onesignal' && (
          <>
            <label>
              <FormattedMessage id="export-modal.onesignal.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setOneSignalInfo({
                  ...oneSignalInfo,
                  apiKey: e.target.value,
                })
              }
              name="onesignal-api-key"
            />
            <label>
              <FormattedMessage id="export-modal.onesignal.app-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setOneSignalInfo({
                  ...oneSignalInfo,
                  appId: e.target.value,
                })
              }
              name="onesignal-app-id"
            />
          </>
        )}
        {selectedItem === 'selzy' && (
          <>
            <label>
              <FormattedMessage id="export-modal.selzy.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setSelzyInfo({
                  ...selzyInfo,
                  apiKey: e.target.value,
                })
              }
              name="selzy-api-key"
            />
          </>
        )}
        {selectedItem === 'pabbly' && (
          <>
            <label>
              <FormattedMessage id="export-modal.pabbly.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setPabblyInfo({
                  ...pabblyInfo,
                  apiKey: e.target.value,
                })
              }
              name="pabbly-api-key"
            />
          </>
        )}
        {selectedItem === 'moosend' && (
          <>
            <label>
              <FormattedMessage id="export-modal.moosend.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMoosendInfo({
                  ...moosendInfo,
                  apiKey: e.target.value,
                })
              }
              name="moosend-api-key"
            />
            <label>
              <FormattedMessage id="export-modal.moosend.sender-email-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMoosendInfo({
                  ...moosendInfo,
                  senderEmail: e.target.value,
                })
              }
              name="moosend-sender-email"
            />
            <label>
              <FormattedMessage id="export-modal.moosend.mailing-list-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMoosendInfo({
                  ...moosendInfo,
                  mailingList: [
                    {
                      ...moosendInfo.mailingList[0],
                      MailingListID: e.target.value,
                    },
                    ...moosendInfo.mailingList.slice(1),
                  ],
                })
              }
              name="moosend-mailing-list-id"
            />
            <label>
              <FormattedMessage id="export-modal.moosend.segment-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMoosendInfo({
                  ...moosendInfo,
                  mailingList: [
                    {
                      ...moosendInfo.mailingList[0],
                      SegmentID: e.target.value,
                    },
                    ...moosendInfo.mailingList.slice(1),
                  ],
                })
              }
              name="moosend-segment-id"
            />
          </>
        )}
        {selectedItem === 'ecomail' && (
          <>
            <label>
              <FormattedMessage id="export-modal.ecomail.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setEcoMailInfo({
                  ...ecoMailInfo,
                  apiKey: e.target.value,
                })
              }
              name="ecomail-api-key"
            />
          </>
        )}
        {selectedItem === 'brevo' && (
          <>
            <label>
              <FormattedMessage id="export-modal.brevo.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setBrevoInfo({
                  ...brevoInfo,
                  apiKey: e.target.value,
                })
              }
              name="icontact-api-key"
            />
          </>
        )}
        {selectedItem === 'icontact' && (
          <>
            <label>
              <FormattedMessage id="export-modal.icontact.account-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setIContactInfo({
                  ...iContactInfo,
                  accountId: e.target.value,
                })
              }
              name="icontact-account-id"
            />
            <label>
              <FormattedMessage id="export-modal.icontact.client-folder-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setIContactInfo({
                  ...iContactInfo,
                  clientFolderId: e.target.value,
                })
              }
              name="icontact-client-folder-id"
            />
            <label>
              <FormattedMessage id="export-modal.icontact.app-id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setIContactInfo({
                  ...iContactInfo,
                  appId: e.target.value,
                })
              }
              name="icontact-app-id"
            />
            <label>
              <FormattedMessage id="export-modal.icontact.username-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setIContactInfo({
                  ...iContactInfo,
                  userName: e.target.value,
                })
              }
              name="icontact-username"
            />
            <label>
              <FormattedMessage id="export-modal.icontact.password-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setIContactInfo({
                  ...iContactInfo,
                  password: e.target.value,
                })
              }
              name="icontact-password"
            />
          </>
        )}
        {selectedItem === 'mailjet' && (
          <>
            <label>
              <FormattedMessage id="export-modal.mailjet.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMailJetInfo({
                  ...mailJetInfo,
                  apiKey: e.target.value,
                })
              }
              name="mailjet-api-secret-key"
            />
            <label>
              <FormattedMessage id="export-modal.mailjet.api-secret-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMailJetInfo({
                  ...mailJetInfo,
                  secretKey: e.target.value,
                })
              }
              name="mailjet-api-secret-key"
            />
            <label>
              <FormattedMessage id="export-modal.mailjet.api-author-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setMailJetInfo({
                  ...mailJetInfo,
                  author: e.target.value,
                })
              }
              name="mailjet-api-author"
            />
            <SelectBox
              isMulti
              onChange={(selectedItems) =>
                setMailJetInfo({
                  ...mailJetInfo,
                  purposes: selectedItems,
                })
              }
              options={[
                {
                  label: 'Marketing',
                  value: 'marketing',
                },
                {
                  label: 'Transactional',
                  value: 'transactional',
                },
                {
                  label: 'Automation',
                  value: 'automation',
                },
                {
                  label: 'Opt-in',
                  value: 'opt-in',
                },
              ]}
              defaultValue={[
                {
                  label: 'Marketing',
                  value: 'marketing',
                },
              ]}
              tooltip=""
              label={intl.formatMessage({
                id: 'export-modal.mailjet.api-purposes-select',
              })}
              placeholder=""
              description=""
            />
          </>
        )}
        {selectedItem === 'sendgrid' && (
          <>
            <label>
              <FormattedMessage id="export-modal.sendgrid.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setSendGridInfo({
                  ...sendGridInfo,
                  apiKey: e.target.value,
                })
              }
              name="sendgrid-api-secret-key"
            />
          </>
        )}
        {selectedItem === 'klaviyo' && (
          <>
            <label>
              <FormattedMessage id="export-modal.klaviyo.api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setKlaviyoInfo({
                  ...klaviyoInfo,
                  privateAPIKey: e.target.value,
                })
              }
              name="klaviyo-api-secret-key"
            />
          </>
        )}
        {selectedItem === 'blueshift' && (
          <>
            <label>
              <FormattedMessage id="export-modal.blueshift.user-api-key-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setBlueShiftInfo({
                  ...blueShiftInfo,
                  userAPIKey: e.target.value,
                })
              }
              name="blueshift-user-api-key"
            />
            <label>
              <FormattedMessage id="export-modal.blueshift.author-name-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setBlueShiftInfo({
                  ...blueShiftInfo,
                  authorName: e.target.value,
                })
              }
              name="blueshift-author-name"
            />
          </>
        )}
        {selectedItem === 'zohomail' && (
          <>
            <label>
              <FormattedMessage id="export-modal.zoho-mail.select-region-label" />
            </label>
            <select
              onChange={(e) => setZohoMailRegion(e.target.value)}
              name="zoho-mail-regions"
              id="zoho-mail-regions">
              {REGIONS.map(({ name, value }, index) => (
                <option key={`zoho-mail-option-${index}`} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </>
        )}
        {selectedItem === 'zohocampaign' && (
          <>
            <label>
              <FormattedMessage id="export-modal.zoho-campaign.select-region-label" />
            </label>
            <select
              onChange={(e) => setZohoCampaignRegion(e.target.value)}
              name="zoho-mail-regions"
              id="zoho-mail-regions">
              {REGIONS.map(({ name, value }, index) => (
                <option key={`zoho-mail-option-${index}`} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </>
        )}
        {selectedItem === 'dotdigital' && (
          <>
            <label>
              <FormattedMessage id="export-modal.dotdigital.select-region-label" />
            </label>
            <select
              onChange={(e) =>
                setDotdigitalInfo({
                  ...dotdigitalInfo,
                  region: e.target.value,
                })
              }
              name="dotdigital-regions"
              id="dotdigital-regions">
              {DOTDIGITAL_REGIONS.map(({ name, value }, index) => (
                <option key={`dotdigital-option-${index}`} value={value}>
                  {name}
                </option>
              ))}
            </select>
            <label>
              <FormattedMessage id="export-modal.dotdigital.username-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setDotdigitalInfo({
                  ...dotdigitalInfo,
                  apiUserName: e.target.value,
                })
              }
              name="dotdigital-username"
            />
            <label>
              <FormattedMessage id="export-modal.dotdigital.password-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setDotdigitalInfo({
                  ...dotdigitalInfo,
                  apiPassword: e.target.value,
                })
              }
              name="dotdigital-password"
            />
          </>
        )}
        {selectedItem === 'sendwithus' && (
          <>
            <label>
              <FormattedMessage id="export-modal.sendwithus.apikey-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setSendwithusInfo({
                  ...sendwithusInfo,
                  apiKey: e.target.value,
                })
              }
              name="sendwithus-apikey"
            />
          </>
        )}
        {selectedItem === 'sendpulse' && (
          <>
            <label>
              <FormattedMessage id="export-modal.sendpulse.id-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setSendpulseInfo({
                  ...sendpulseInfo,
                  id: e.target.value,
                })
              }
              name="sendwithus-id"
            />
            <label>
              <FormattedMessage id="export-modal.sendpulse.secret-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setSendpulseInfo({
                  ...sendpulseInfo,
                  secret: e.target.value,
                })
              }
              name="sendwithus-secret"
            />
          </>
        )}
        {selectedItem === 'rule' && (
          <>
            <label>
              <FormattedMessage id="export-modal.rule.apikey-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setRuleInfo({
                  ...ruleInfo,
                  apiKey: e.target.value,
                })
              }
              name="rule-apikey"
            />
            <label>
              <FormattedMessage id="export-modal.rule.from-name-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setRuleInfo({
                  ...ruleInfo,
                  fromName: e.target.value,
                })
              }
              name="rule-from-name"
            />
            <label>
              <FormattedMessage id="export-modal.rule.from-email-input" />
            </label>
            <input
              type="text"
              onChange={(e) =>
                setRuleInfo({
                  ...ruleInfo,
                  fromEmail: e.target.value,
                })
              }
              name="rule-from-email"
            />
          </>
        )}
        <div className="export-modal-email-provider__actions">
          {selectedItem === 'elasticemail' && (
            <button
              disabled={isLoading || !elasticEmailInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToElasticEmail({
                      templateId,
                      payload: elasticEmailInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Elastic Email!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'onesignal' && (
            <button
              disabled={
                isLoading || !oneSignalInfo.apiKey || !oneSignalInfo.appId
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToOneSignal({
                      templateId,
                      payload: oneSignalInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the OneSignal!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'selzy' && (
            <button
              disabled={isLoading || !selzyInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToSelzy({
                      templateId,
                      payload: selzyInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Selzy!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'pabbly' && (
            <button
              disabled={isLoading || !pabblyInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToPabbly({
                      templateId,
                      payload: pabblyInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Pabbly!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'moosend' && (
            <button
              disabled={
                isLoading ||
                !moosendInfo.apiKey ||
                !moosendInfo.mailingList[0].MailingListID ||
                !moosendInfo.mailingList[0].SegmentID ||
                !moosendInfo.senderEmail
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToMoosend({
                      templateId,
                      payload: moosendInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Moosend!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'rule' && (
            <button
              disabled={
                isLoading ||
                !ruleInfo.apiKey ||
                !ruleInfo.fromEmail ||
                !ruleInfo.fromName
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToRule({
                      templateId,
                      payload: ruleInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Rule!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'sendpulse' && (
            <button
              disabled={isLoading || !sendpulseInfo.id || !sendpulseInfo.secret}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToSendpulse({
                      templateId,
                      payload: sendpulseInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the SendPulse!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'sendwithus' && (
            <button
              disabled={isLoading || !sendwithusInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToSendwithus({
                      templateId,
                      payload: sendwithusInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the SendWithUs!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'ecomail' && (
            <button
              disabled={isLoading || !ecoMailInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToEcoMail({
                      templateId,
                      payload: ecoMailInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the EcoMail!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'brevo' && (
            <button
              disabled={isLoading || !brevoInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToBrevo({
                      templateId,
                      payload: brevoInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Brevo!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'klaviyo' && (
            <button
              disabled={isLoading || !klaviyoInfo.privateAPIKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToKlaviyo({
                      templateId,
                      payload: klaviyoInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Klaviyo!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'icontact' && (
            <button
              disabled={
                isLoading ||
                !iContactInfo.accountId ||
                !iContactInfo.appId ||
                !iContactInfo.clientFolderId ||
                !iContactInfo.userName ||
                !iContactInfo.password
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToIContact({
                      templateId,
                      payload: iContactInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the iContact!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'mailjet' && (
            <button
              disabled={
                isLoading ||
                !mailJetInfo.apiKey ||
                !mailJetInfo.secretKey ||
                !mailJetInfo.author ||
                mailJetInfo.purposes.length < 1
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToMailJet({
                      templateId,
                      payload: mailJetInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the MailJet!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'sendgrid' && (
            <button
              disabled={isLoading || !sendGridInfo.apiKey}
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToSendGrid({
                      templateId,
                      payload: sendGridInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the SendGrid!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'blueshift' && (
            <button
              disabled={
                isLoading ||
                !blueShiftInfo.userAPIKey ||
                !blueShiftInfo.authorName
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToBlueShift({
                      templateId,
                      payload: blueShiftInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the BlueShift!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'dotdigital' && (
            <button
              disabled={
                isLoading ||
                !dotdigitalInfo.apiUserName ||
                !dotdigitalInfo.apiPassword
              }
              onClick={async () => {
                try {
                  const res = await dispatch(
                    exportToDotdigital({
                      templateId,
                      payload: dotdigitalInfo,
                    }),
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    showSuccessMessage(
                      'Your email template has been successfully exported to the Dotdigital!',
                    );
                  }
                } catch {
                  showErrorMessage(getErrorText());
                } finally {
                  setIsLoading(false);
                  setSelectedItem('');
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'zohomail' && (
            <button
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);

                const payload = {
                  name: userEmail,
                  type: 'ZOHOMAIL',
                  region: zohoMailRegion,
                };

                try {
                  const { authLink } = await dispatch(
                    accountLookup({ userId, payload }),
                  ).unwrap();

                  openZohoMailAuthPopup(
                    authLink,
                    dispatch,
                    saveZohoMailDraft,
                    templateId,
                    showSuccessMessage,
                    showErrorMessage,
                    getErrorText,
                    setIsLoading,
                    setSelectedItem,
                    zohoMailRegion,
                  );
                } catch {
                  showErrorMessage(getErrorText());
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'zohocampaign' && (
            <button
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);

                const payload = {
                  name: userEmail,
                  type: 'ZOHOCAMPAIGN',
                  region: zohoCampaignRegion,
                };

                try {
                  const { authLink } = await dispatch(
                    accountLookup({ userId, payload }),
                  ).unwrap();

                  openZohoCampaignAuthPopup(
                    authLink,
                    dispatch,
                    saveZohoCampaignDraft,
                    templateId,
                    showSuccessMessage,
                    showErrorMessage,
                    getErrorText,
                    setIsLoading,
                    setSelectedItem,
                    zohoCampaignRegion,
                  );
                } catch {
                  showErrorMessage(getErrorText());
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ExportModalEmailProvider.propTypes = {
  templateId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  selectedItem: PropTypes.string,
  setSelectedItem: PropTypes.func.isRequired,
};

export default ExportModalEmailProvider;
